import type { ReactNode } from 'react';
import {
  FileText,
  Clipboard,
  PenTool,
  MessageCircle,
  RefreshCw,
} from 'lucide-react';
import type { TipoAvaliacao, StatusAvaliacao, Avaliacao } from '../../api/hooks/useAvaliacoes';

export const TIPO_LABELS: Record<TipoAvaliacao, { label: string; color: string; icon: ReactNode }> = {
  PROVA: { label: 'Prova', color: '#dc2626', icon: <FileText size={16} /> },
  TRABALHO: { label: 'Trabalho', color: '#2563eb', icon: <Clipboard size={16} /> },
  ATIVIDADE: { label: 'Atividade', color: '#059669', icon: <PenTool size={16} /> },
  PARTICIPACAO: { label: 'Participacao', color: '#7c3aed', icon: <MessageCircle size={16} /> },
  RECUPERACAO: { label: 'Recuperacao', color: '#d97706', icon: <RefreshCw size={16} /> },
};

export const STATUS_LABELS: Record<StatusAvaliacao, { label: string; color: string; bg: string }> = {
  RASCUNHO: { label: 'Rascunho', color: '#6b7280', bg: 'rgba(107,114,128,0.12)' },
  AGENDADA: { label: 'Agendada', color: '#3b82f6', bg: 'rgba(59,130,246,0.12)' },
  EM_APLICACAO: { label: 'Em aplicacao', color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
  CONCLUIDA: { label: 'Concluida', color: '#22c55e', bg: 'rgba(34,197,94,0.12)' },
  CANCELADA: { label: 'Cancelada', color: '#ef4444', bg: 'rgba(239,68,68,0.12)' },
};

export const BIMESTRES = [
  { value: 1, label: '1º Bim' },
  { value: 2, label: '2º Bim' },
  { value: 3, label: '3º Bim' },
  { value: 4, label: '4º Bim' },
];

export const AVALIACAO_EDICAO_DIAS_PROFESSOR = 7;

export function formatDataCurta(d?: string) {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  } catch {
    return '';
  }
}

export function metaAvaliacao(av: Avaliacao) {
  const parts = [
    av.turma?.nome,
    av.materia?.nome,
    BIMESTRES.find((b) => b.value === av.bimestre)?.label,
    formatDataCurta(av.dataAplicacao),
    `Max ${av.valorMaximo}`,
  ].filter(Boolean);
  return parts.join(' · ');
}

export function iniciaisAluno(nome: string) {
  return nome
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();
}

export function hasFotoReal(fotoUrl?: string | null) {
  if (!fotoUrl) return false;
  const url = fotoUrl.toLowerCase();
  return !url.includes('i.pravatar.cc') && !url.includes('ui-avatars.com');
}

function inicioDoDia(d: Date) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function dentroDaJanelaProfessor(av: Avaliacao) {
  const ref = av.dataAplicacao ? new Date(av.dataAplicacao) : new Date(av.criadoEm);
  const hoje = inicioDoDia(new Date());
  const dataRef = inicioDoDia(ref);
  const diffDias = Math.floor((hoje.getTime() - dataRef.getTime()) / 86400000);
  return diffDias <= AVALIACAO_EDICAO_DIAS_PROFESSOR;
}

export function professorPodeEditarAvaliacao(av: Avaliacao, modo: 'notas' | 'metadados' | 'status') {
  if (av.status === 'CANCELADA') return false;
  if (modo === 'status') {
    return av.status !== 'CONCLUIDA' && (av as any).status !== 'CANCELADA';
  }
  if (av.status === 'CONCLUIDA') return false;
  return dentroDaJanelaProfessor(av);
}

function formatHistoricoValor(v: number | null | undefined) {
  if (v == null) return '—';
  return String(v).replace('.', ',');
}

export function linhaHistoricoNota(h: {
  acao: string;
  valorAnterior: number | null;
  valorNovo: number | null;
  criadoEm: string;
  aluno?: { nome: string };
  alteradoPor?: { nome: string };
}) {
  const quando = formatDataCurta(h.criadoEm);
  const aluno = h.aluno?.nome || 'Aluno';
  const quem = h.alteradoPor?.nome || 'Usuario';
  if (h.acao === 'CREATE') {
    return `${quando} · ${aluno}: nota ${formatHistoricoValor(h.valorNovo)} (${quem})`;
  }
  return `${quando} · ${aluno}: ${formatHistoricoValor(h.valorAnterior)} → ${formatHistoricoValor(h.valorNovo)} (${quem})`;
}

export function motivoBloqueioEdicao(av: Avaliacao): string | null {
  if (av.status === 'CANCELADA') return 'Avaliacao cancelada.';
  if (av.status === 'CONCLUIDA') {
    return 'Avaliacao concluida: alteracoes somente pela coordenacao.';
  }
  if (!dentroDaJanelaProfessor(av)) {
    return `Prazo de edicao expirado (${AVALIACAO_EDICAO_DIAS_PROFESSOR} dias). Solicite a coordenacao.`;
  }
  return null;
}
